import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CoinGeckoAPI } from '../services/api';
import { logger } from '../services/logger';
import type { TokenInputProps, Coin } from '../types';

interface ExtendedTokenInputProps extends TokenInputProps {
  placeholder?: string;
  compact?: boolean;
}

/**
 * TokenInput - Autocomplete input for adding tokens
 *
 * Features:
 * - Real-time search using CoinGecko /search API
 * - Debounced search to reduce API calls
 * - Fallback to local coins list
 * - Keyboard navigation (Enter to select, Escape to close)
 * - Smart sorting (exact matches first)
 * - Loading state handling
 * - Prevents blur on item click
 * - Uses React Portal to escape stacking context
 *
 * @param onAdd - Callback when token selected (coinId, symbol)
 * @param coins - Array of available coins (fallback) { id, symbol, name }
 * @param map - Optional coin map (not used currently)
 * @param placeholder - Optional custom placeholder text
 * @param compact - Optional compact mode for toolbar integration
 */
export function TokenInput({ onAdd, coins, map, placeholder, compact }: ExtendedTokenInputProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Coin[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update position when showing and on scroll/resize
  useEffect(() => {
    if (!showSuggestions || !inputRef.current) return;

    const updatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 320)
        });
      }
    };

    updatePosition(); // Initial position
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSuggestions]);

  // Debounced search using CoinGecko API
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
      return;
    }

    // Show searching state
    setIsSearching(true);

    // Debounce search (500ms)
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        // Use CoinGecko /search API for real-time search
        const result = await CoinGeckoAPI.searchCoins(query);

        if (result.coins && result.coins.length > 0) {
          // Limit to top 10 results
          const topResults = result.coins.slice(0, 10);
          setSuggestions(topResults);
          setShowSuggestions(true);
          logger.info('TokenInput', `✅ Found ${topResults.length} results for "${query}"`);
        } else {
          // Fallback to local filtering if API returns no results
          if (coins && coins.length > 0) {
            const q = query.toLowerCase();
            const localMatches = coins
              .filter(c =>
                c.symbol?.toLowerCase().includes(q) ||
                c.name?.toLowerCase().includes(q)
              )
              .sort((a, b) => {
                // Prioritize exact symbol matches
                const aS = a.symbol?.toLowerCase().startsWith(q);
                const bS = b.symbol?.toLowerCase().startsWith(q);
                if (aS !== bS) return (bS ? 1 : 0) - (aS ? 1 : 0);
                return (a.symbol || '').localeCompare(b.symbol || '');
              })
              .slice(0, 10);

            setSuggestions(localMatches);
            setShowSuggestions(localMatches.length > 0);
            logger.info('TokenInput', `📋 Using local fallback: ${localMatches.length} results for "${query}"`);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      } catch (error) {
        logger.warn('TokenInput', `⚠️ Search API failed, using local fallback`, error);

        // Fallback to local filtering on error
        if (coins && coins.length > 0) {
          const q = query.toLowerCase();
          const localMatches = coins
            .filter(c =>
              c.symbol?.toLowerCase().includes(q) ||
              c.name?.toLowerCase().includes(q)
            )
            .sort((a, b) => {
              const aS = a.symbol?.toLowerCase().startsWith(q);
              const bS = b.symbol?.toLowerCase().startsWith(q);
              if (aS !== bS) return (bS ? 1 : 0) - (aS ? 1 : 0);
              return (a.symbol || '').localeCompare(b.symbol || '');
            })
            .slice(0, 10);

          setSuggestions(localMatches);
          setShowSuggestions(localMatches.length > 0);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } finally {
        setIsSearching(false);
      }
    }, 500); // 500ms debounce

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, coins]);

  const handleSelect = (coin: Coin): void => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setIsSearching(false);
    onAdd(coin.id, coin.symbol.toUpperCase());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && suggestions.length === 1) {
      e.preventDefault();
      handleSelect(suggestions[0]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleBlur = (): void => {
    // Delay to allow click on item to work
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const isCoinsLoaded = coins && coins.length > 0;

  // Dropdown rendered with Portal to escape stacking context
  const dropdownPortal = showSuggestions && suggestions.length > 0 && createPortal(
    <div
      ref={listRef}
      className="autocomplete-list autocomplete-list-portal"
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        maxWidth: '400px',
        zIndex: 100000
      }}
      onMouseDown={(e) => e.preventDefault()} // Prevent input blur
    >
      {suggestions.map(coin => (
        <div
          key={coin.id}
          className="autocomplete-item"
          onClick={() => handleSelect(coin)}
        >
          <div className="autocomplete-item-symbol">
            {coin.symbol.toUpperCase()}
          </div>
          <div className="autocomplete-item-name">
            {coin.name}
          </div>
        </div>
      ))}
    </div>,
    document.body
  );

  const defaultPlaceholder = isSearching ? "Recherche..." :
    isCoinsLoaded ? "Ajouter (FLUX, ONDO, BTC...)" :
    "Chargement...";

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: compact ? '100%' : 'auto' }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        onBlur={handleBlur}
        placeholder={placeholder || defaultPlaceholder}
        style={{
          minWidth: compact ? 'auto' : '280px',
          width: compact ? '100%' : 'auto',
          height: compact ? '34px' : 'auto',
          fontSize: compact ? '13px' : '14px',
          padding: compact ? '6px 12px' : undefined
        }}
        disabled={!isCoinsLoaded}
        title={
          isSearching ? "Recherche en cours..." :
          !isCoinsLoaded ? "Chargement de la liste des cryptos en cours..." :
          "Recherchez par symbole (FLUX) ou nom (Ondo)"
        }
      />

      {/* Dropdown rendered via Portal at the end of document.body */}
      {dropdownPortal}

      {/* Loading indicator - hide in compact mode */}
      {!isCoinsLoaded && !compact && (
        <div style={{
          fontSize: '11px',
          color: '#f59e0b',
          marginTop: '4px',
          fontWeight: '500'
        }}>
          ⏳ Chargement de la liste des cryptos...
        </div>
      )}
    </div>
  );
}
